import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CloudService } from 'src/app/services/cloud.service';
import { SeoService } from 'src/app/core/services/seo.service';
import { CalendlyService } from 'src/app/services/calendly.service';
import { environment } from 'src/environments/environment';
import { renderRichText as renderStoryblokRichText } from '@storyblok/richtext/static';

const preferredSourcesScriptId = 'google-preferred-sources-publisher';
const preferredSourcesScriptSrc = 'https://news.google.com/swg/js/v1/publisher.js';
const preferredSourcesTheme = 'light';

interface PreferredSourceApi {
  init(options?: { theme?: 'light' | 'dark'; lang?: string }): void;
}

type PreferredSourceCallback = (preferredSource: PreferredSourceApi) => void;

interface PreferredSourceQueue {
  push(callback: PreferredSourceCallback): unknown;
}

interface PreferredSourceWindow extends Window {
  PREFERRED_SOURCE?: PreferredSourceQueue;
}

interface BlogDetail {
  id: string;
  title: string;
  category: string;
  readTime: string;
  date: string;
  author: string;
  authorImageUrl: string;
  authorImageAlt: string;
  excerpt: string;
  imageUrl: string;
  imageAlt: string;
  datePublished: string;
}

@Component({
  selector: 'app-blog-details',
  imports: [],
  templateUrl: './blog-details.component.html',
  styleUrls: ['./blog-details.component.css']
})
export class BlogDetailsComponent implements OnInit, OnDestroy {

  public calendlyUrl = environment.CALENDLY_URL;
  post: BlogDetail | null = null;
  renderedBody = '';
  loading = false;
  errorMessage = '';
  private routeSub?: Subscription;
  private fetchSub?: Subscription;
  private preferredSourcesRenderTimeout?: ReturnType<typeof setTimeout>;

  contactForm = new FormGroup({
    name: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.required, Validators.email]),
    company: new FormControl(''),
    message: new FormControl('', Validators.required)
  });

  isSubmitting = false;
  submitted = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cloudService: CloudService,
    private seoService: SeoService,
    private calendly: CalendlyService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) { }

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe(params => {
      const id = (params.get('id') || '').trim();
      this.loadBlogPost(id);
      this.scrollToTop();
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.fetchSub?.unsubscribe();
    this.clearPreferredSourcesRenderTimeout();
  }

  private loadBlogPost(blogUrl: string): void {
    this.clearPreferredSourcesRenderTimeout();

    if (!blogUrl) {
      this.loading = false;
      this.errorMessage = 'Invalid blog URL.';
      this.post = null;
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.post = null;
    this.renderedBody = '';
    this.fetchSub?.unsubscribe();

    this.fetchSub = this.cloudService.getBlogDetails(blogUrl).subscribe({
      next: (data: any) => {
        const story = data?.object?.story || data?.story || null;
        this.post = this.mapBlogDetail(story);
        this.renderRichText(story?.content?.body);
        if (this.post) {
          const routeUrl = `/blog/${blogUrl}`;
          this.seoService.updateRouteSeo(routeUrl, {
            title: this.post.title,
            description: this.post.excerpt,
            type: 'article',
            image: this.post.imageUrl,
            author: this.post.author,
            publishedTime: this.post.datePublished,
            breadcrumbs: [
              { name: 'Home', url: 'https://www.reve.cloud/' },
              { name: 'Blog', url: 'https://www.reve.cloud/blog' },
              { name: this.post.title, url: `https://www.reve.cloud${routeUrl}` }
            ]
          }, {
            blog: {
              title: this.post.title,
              excerpt: this.post.excerpt,
              author: this.post.author,
              datePublished: this.post.datePublished,
              image: this.post.imageUrl
            }
          });
        }
        this.loading = false;
        if (this.post) {
          this.schedulePreferredSourcesRender();
        }
      },
      error: (e) => {
        this.loading = false;
        this.errorMessage = e?.error?.message || 'Unable to load blog details.';
        this.post = null;
        this.renderedBody = '';
      }
    });
  }

  private mapBlogDetail(story: any): BlogDetail | null {
    if (!story) {
      return null;
    }

    const content = story?.content ?? {};
    const readTimeRaw = content?.read_time;
    const readTimeValue = Number(readTimeRaw);
    const readTime = Number.isFinite(readTimeValue) && readTimeValue > 0
      ? `${readTimeValue} min read`
      : '5 min read';
    const publishedDate = content?.post_date || '';

    return {
      id: String(story?.slug || story?.uuid || story?.id || ''),
      title: content?.heading || story?.name || 'Untitled Blog',
      category: content?.category || 'General',
      readTime,
      date: this.formatDate(publishedDate),
      author: content?.author_name || 'Rêve AI Engineers',
      authorImageUrl: content?.author_image?.filename || '',
      authorImageAlt: content?.author_image?.alt || content?.author_name || 'Author',
      excerpt: content?.short_description || 'No description available.',
      imageUrl: content?.banner?.filename || 'assets/images/placeholder.png',
      imageAlt: content?.banner?.alt || content?.heading || 'Blog image',
      datePublished: publishedDate
    };
  }

  private renderRichText(body: any): void {
    try {
      this.renderedBody = renderStoryblokRichText(body || { type: 'doc', content: [] });
      if (!this.renderedBody && this.post?.excerpt) {
        this.renderedBody = `<p>${this.post.excerpt}</p>`;
      }
    } catch {
      this.renderedBody = this.post?.excerpt ? `<p>${this.post.excerpt}</p>` : '';
    }
  }

  private formatDate(dateValue: string): string {
    if (!dateValue) {
      return '';
    }

    const normalized = typeof dateValue === 'string' ? dateValue.replace(' ', 'T') : dateValue;
    const date = new Date(normalized);

    if (Number.isNaN(date.getTime())) {
      return String(dateValue);
    }

    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric'
    });
  }

  backToBlog(): void {
    this.router.navigate(['/blog']);
    this.scrollToTop();
  }

  openCalendlyPopup() {
    return this.calendly.openPopup(this.calendlyUrl);
  }

  onSubmit(): void {
    if (!this.contactForm.valid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    setTimeout(() => {
      this.isSubmitting = false;
      this.submitted = true;
      this.contactForm.reset();
      setTimeout(() => {
        this.submitted = false;
      }, 5000);
    }, 1500);
  }

  private scrollToTop(): void {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }

  private schedulePreferredSourcesRender(): void {
    this.clearPreferredSourcesRenderTimeout();

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.preferredSourcesRenderTimeout = setTimeout(() => {
      this.renderPreferredSourcesButton();
    }, 0);
  }

  private clearPreferredSourcesRenderTimeout(): void {
    if (this.preferredSourcesRenderTimeout !== undefined) {
      clearTimeout(this.preferredSourcesRenderTimeout);
      this.preferredSourcesRenderTimeout = undefined;
    }
  }

  private renderPreferredSourcesButton(): void {
    if (this.hasRenderedPreferredSourcesButton()) {
      return;
    }

    this.queuePreferredSourcesRender();
    this.ensurePreferredSourcesScript();
  }

  private queuePreferredSourcesRender(): void {
    const preferredSourceWindow = this.document.defaultView as PreferredSourceWindow | null;
    if (!preferredSourceWindow) {
      return;
    }

    const renderStandardButton: PreferredSourceCallback = preferredSource => {
      if (!this.hasRenderedPreferredSourcesButton()) {
        preferredSource.init({ theme: preferredSourcesTheme });
      }
    };

    if (preferredSourceWindow.PREFERRED_SOURCE) {
      preferredSourceWindow.PREFERRED_SOURCE.push(renderStandardButton);
      return;
    }

    preferredSourceWindow.PREFERRED_SOURCE = [renderStandardButton];
  }

  private ensurePreferredSourcesScript(): void {
    const existingScript = this.document.getElementById(preferredSourcesScriptId)
      || this.document.querySelector(`script[src="${preferredSourcesScriptSrc}"]`);

    if (existingScript) {
      return;
    }

    this.document.head.appendChild(this.createPreferredSourcesScript());
  }

  private createPreferredSourcesScript(): HTMLScriptElement {
    const script = this.document.createElement('script');
    script.id = preferredSourcesScriptId;
    script.async = true;
    script.setAttribute('preferred-sources-control', 'manual');
    script.src = preferredSourcesScriptSrc;
    return script;
  }

  private hasRenderedPreferredSourcesButton(): boolean {
    const button = this.document.querySelector('.blog-preferred-source [google-add-preferred-source-btn]');
    return !!button && (button.childElementCount > 0 || !!button.shadowRoot?.childElementCount);
  }

}
